import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import RoundedIcon from '@/components/ui/RoundedIcon';
import Icon from '@/components/ui/Icon';
import { type Swiper, type SwiperOptions } from 'swiper/types';
import { v4 as uuid4 } from 'uuid';
import { SwiperContainer, register } from 'swiper/element/bundle';
import classnames from 'classnames';
import throttle from 'lodash/throttle';
import Shimmer from '@/components/ui/Shimmer';
import CardBuilder from '@/components/ui/Cards/CardBuilder';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import { ScreenSizeQueries } from '@/constants/breakpoints';
import { ISearchResultItem, ItemListAnalyticsModel } from '@/@types/client-api';
import LoadingDots from '../Buttons/LoadingDots';

enum Type {
  Standard = 'standard',
  Faculty = 'faculty',
  Library = 'library'
}

enum Size {
  Small = 'small',
  Medium = 'medium',
  Large = 'large'
}

export interface ProductCarouselProps {
  type?: `${Type}`;
  cards: Record<string, unknown>[];
  labelFindMore?: string;
  onClickFindMore?: () => void;
  onRefreshParentData?: () => void;
  isLoading?: boolean;
  isLazyLoadingCards?: boolean;
  enumerateCards?: boolean;
  SetCarouselViewport?: (start: number, end: number, isSwiped?: boolean) => void;
  createAnaliticsItem?: (card: Record<string, unknown>) => void;
  onReachEndOfList?: (slide: number) => void;
  initialSlide?: number;
  listId?: string;
  listName?: string;
  itemListAnalyticsModel?: ItemListAnalyticsModel;
}

const ProductCarousel: React.FC<ProductCarouselProps> = ({
  type = Type.Standard,
  cards,
  labelFindMore,
  onClickFindMore,
  onRefreshParentData,
  isLoading = false,
  isLazyLoadingCards = false,
  enumerateCards = false,
  SetCarouselViewport,
  createAnaliticsItem,
  onReachEndOfList,
  initialSlide = 0,
  listId,
  listName,
  itemListAnalyticsModel
}) => {
  const [swiper, setSwiper] = useState<Swiper | null>(null);
  const [isPrevVisible, setIsPrevVisible] = useState(false);
  const [isNextVisible, setIsNextVisible] = useState(false);

  const sm = useMediaQuery(ScreenSizeQueries.sm);
  const md = useMediaQuery(ScreenSizeQueries.md);

  const containerRef = useRef(null);
  const swiperRef = useRef(null);
  const shouldAutoSwipe = useRef(false);
  const cardsCount = cards?.length || 0;
  const navPrevId = useMemo(() => `nav-prev-${uuid4()}`, []);
  const navNextId = useMemo(() => `nav-next-${uuid4()}`, []);
  let cardsSize: `${Size}` = Size.Small;

  const GetCarouselViewport = useCallback(
    (swiper: Swiper, isSwiped?: boolean) => {
      const containerWidth = swiper?.el?.clientWidth;
      const cardWidth = swiper.slides[swiper?.activeIndex ?? 0]?.clientWidth;

      const firstVisibleSlideIndex = swiper.activeIndex;
      const visibleSlides = Math.max(Math.floor(containerWidth / Math.max(cardWidth ?? 0, 50)), 1);

      const lastVisibleSlideIndex = Math.min(
        firstVisibleSlideIndex + visibleSlides - 1,
        swiper.slides?.length - 1
      );

      if (SetCarouselViewport) {
        SetCarouselViewport(firstVisibleSlideIndex, lastVisibleSlideIndex, isSwiped);
      }
    },
    [SetCarouselViewport] // Dependencies
  );

  const swiperSlideClasses = classnames('h-auto', {
    'w-auto': !sm && !md,
    'w-[37.5%]': sm,
    'w-fit': md
  });

  useEffect(() => {
    if (!swiperRef.current) return;
    register();

    const params: SwiperOptions = {
      slidesPerGroupAuto: true,
      lazyPreloadPrevNext: 4,
      maxBackfaceHiddenSlides: 1,
      spaceBetween: 24,
      watchSlidesProgress: true,
      navigation: {
        enabled: true,
        prevEl: `#${navPrevId}`,
        nextEl: `#${navNextId}`
      },
      initialSlide,
      autoHeight: false,
      on: {
        init(swiper) {
          setSwiper(swiper);
          GetCarouselViewport(swiper);
        },
        slideChange(swiper) {
          GetCarouselViewport(swiper, true);
        },
        navigationNext(swiper) {
          if (swiper.isEnd && onReachEndOfList) {
            swiper.allowSlideNext = false;
            const newIndex = swiper.activeIndex + swiper.slidesPerViewDynamic();
            onReachEndOfList(newIndex);
            shouldAutoSwipe.current = true;
          }
        }
      },
      breakpoints: {
        0: {
          slidesPerView: 'auto'
        },
        889: {
          slidesPerView: 3,
          slidesPerGroup: 3
        },
        1049: {
          slidesPerView: 4,
          slidesPerGroup: 4
        }
      }
    };

    Object.assign(swiperRef.current, params);
    const currentSwiper = swiperRef.current as SwiperContainer;
    currentSwiper.initialize();
  }, [navNextId, navPrevId, GetCarouselViewport, initialSlide, onReachEndOfList]);

  useEffect(() => {
    if (!isLazyLoadingCards) {
      // Add this to ensure initialSlide works
      const timeout = setTimeout(() => {
        if (swiper && shouldAutoSwipe.current) {
          swiper.allowSlideNext = true;
          swiper.slideTo(initialSlide, 200, false);
          shouldAutoSwipe.current = false;
        }
      }, 1);

      return () => clearTimeout(timeout);
    }
  }, [initialSlide, isLazyLoadingCards, swiper]);

  if (type !== Type.Library && cardsCount === 1) {
    cardsSize = Size.Large;
  } else if (type !== Type.Library && cardsCount === 2) {
    cardsSize = Size.Medium;
  }

  const cardClasses = classnames('w-full !max-w-none');

  const navClasses = classnames(
    'group/navBtn',
    'absolute top-1/2 z-1 hidden w-[60px] -translate-y-1/2 overflow-hidden rounded-full opacity-0 backdrop-blur-md transition-opacity',
    'disabled:pointer-events-none disabled:opacity-0',
    'hover:opacity-100',
    'lg:flex'
  );

  const navIconClasses = classnames(
    'w-[60px] bg-black/30 transition-colors',
    'group-hover/navBtn:bg-black/40'
  );

  const FindMoreCard = () => {
    return (
      !!labelFindMore &&
      !!onClickFindMore && (
        <button
          className={classnames(
            'relative flex size-full h-[400px] max-w-[300px] items-center justify-center rounded-lg bg-gray-lightest p-6 transition',
            'hover:bg-gray-light focus:bg-gray-light',
            cardClasses
          )}
          onClick={onClickFindMore}
        >
          <div className="flex w-screen items-center justify-center gap-2">
            <span className="text-1 font-medium">{labelFindMore}</span>
            <Icon name="search" size="medium" />
          </div>
        </button>
      )
    );
  };

  const handleMouseMove = throttle((e: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
    const container = containerRef.current as HTMLDivElement | null;
    if (container) {
      const rect = container.getBoundingClientRect();
      const mouseX = e.clientX - rect.left;

      if (swiper?.isBeginning) {
        setIsPrevVisible(false);
        setIsNextVisible(true);
      } else if (swiper?.isEnd) {
        setIsPrevVisible(true);
        setIsNextVisible(false);
      } else if (mouseX < rect.width / 2) {
        setIsPrevVisible(true);
        setIsNextVisible(false);
      } else {
        setIsPrevVisible(false);
        setIsNextVisible(true);
      }
    }
  }, 100);

  const handleMouseLeave = () => {
    setTimeout(() => {
      setIsPrevVisible(false);
      setIsNextVisible(false);
    }, 250);
  };

  return (
    <>
      {isLoading ? (
        <div className="flex gap-6 overflow-visible">
          {Array.from({ length: 5 }).map((_, index) => (
            <Shimmer
              key={index}
              className={classnames(
                'size-full h-[400px] rounded-lg',
                'min-w-[75%]',
                'sm:min-w-[37.5%]',
                'md:min-w-[calc(25%-18px)]'
              )}
            />
          ))}
        </div>
      ) : (
        <>
          {cardsCount > 0 ? (
            <>
              {cardsCount >= 3 || type === Type.Library ? (
                <div
                  ref={containerRef}
                  className="relative"
                  onMouseMove={handleMouseMove}
                  onMouseLeave={handleMouseLeave}
                  data-component={'ProductCarousel'}
                >
                  <swiper-container
                    init={false}
                    class={classnames('swiper-no-overflow', {
                      'opacity-50': isLazyLoadingCards
                    })}
                    ref={swiperRef}
                  >
                    {cards.map((card, index) => (
                      <swiper-slide key={index} class={swiperSlideClasses}>
                        <CardBuilder
                          cardProps={card}
                          size={cardsSize}
                          index={index}
                          showIndexOverlay={enumerateCards}
                          widthAuto={md}
                          type={type}
                          heightAuto={true}
                          clickOnCardContent={createAnaliticsItem}
                          itemAnalyticsModel={(card as ISearchResultItem)?.itemAnalyticsModel}
                          onRefreshParentData={onRefreshParentData}
                          listId={listId}
                          listName={listName}
                          itemListAnalyticsModel={itemListAnalyticsModel}
                        />
                      </swiper-slide>
                    ))}
                    {cardsCount <= 1 && (
                      <swiper-slide class={swiperSlideClasses}>
                        <FindMoreCard />
                      </swiper-slide>
                    )}
                  </swiper-container>
                  <button
                    id={navPrevId}
                    className={classnames(navClasses, 'left-[-30px]', {
                      'opacity-0': !isPrevVisible,
                      'opacity-100': isPrevVisible
                    })}
                  >
                    <RoundedIcon
                      color="black"
                      icon={{ name: 'arrow-left', size: 'large' }}
                      className={navIconClasses}
                    />
                  </button>
                  <button
                    id={navNextId}
                    disabled={!!isLazyLoadingCards}
                    className={classnames(navClasses, 'right-[-30px]', {
                      'opacity-0': !isNextVisible && !isLazyLoadingCards,
                      'opacity-100': isNextVisible || !!isLazyLoadingCards
                    })}
                  >
                    {isLazyLoadingCards ? (
                      <span
                        className={classnames(
                          'flex aspect-square w-10 items-center justify-center overflow-hidden rounded-full bg-black',
                          navIconClasses
                        )}
                      >
                        <LoadingDots dotSize="small" />
                      </span>
                    ) : (
                      <RoundedIcon
                        color="black"
                        icon={{ name: 'arrow-right', size: 'large' }}
                        className={navIconClasses}
                      />
                    )}
                  </button>
                </div>
              ) : (
                <div
                  className={classnames('relative flex flex-col gap-4', 'sm700:flex-row')}
                  data-component={'ProductCarousel'}
                >
                  {cards.map((card, index) => (
                    <CardBuilder
                      className="*:!max-w-none"
                      key={index}
                      cardProps={card}
                      size={cardsSize}
                      index={index}
                      showIndexOverlay={enumerateCards}
                      widthAuto={true}
                      clickOnCardContent={createAnaliticsItem}
                      itemAnalyticsModel={(card as ISearchResultItem)?.itemAnalyticsModel}
                      onRefreshParentData={onRefreshParentData}
                      listId={listId}
                      listName={listName}
                      itemListAnalyticsModel={itemListAnalyticsModel}
                    />
                  ))}
                </div>
              )}
            </>
          ) : null}
        </>
      )}
    </>
  );
};

export default ProductCarousel;
